export const userLists = {
  STAFF: 'staff',
  EXTERNAL: 'external',
} as const

export type UserList = typeof userLists[keyof typeof userLists]

export const userStatuses = {
  ACTIVE: 'active',
  UNCONFIRMED: 'unconfirmed',
  DEACTIVATED: 'deactivated',
} as const

export type UserStatus = typeof userStatuses[keyof typeof userStatuses]

export const isUserStatus = (value: string): value is UserStatus =>
  (Object.values(userStatuses) as readonly string[]).includes(value)

export const userRoles = {
  ADMIN: 'admin',
  STAFF: 'staff',
  CONTRIBUTOR: 'contributor',
  PULSE: 'pulse',
} as const

export type UserRole = typeof userRoles[keyof typeof userRoles]

export const isUserRole = (value: string): value is UserRole =>
  (Object.values(userRoles) as readonly string[]).includes(value)

// The values of UserManagement::Vocabulary::LAST_LOGIN_FILTERS. Unlike the
// role and the status, no table column carries them, so the filter is the only
// place they appear.
export const userLastLoginFilters = {
  OVER_1_MONTH: 'over_1_month',
  OVER_1_YEAR: 'over_1_year',
  OVER_2_YEARS: 'over_2_years',
  NEVER: 'never',
} as const

export type UserLastLoginFilter =
  typeof userLastLoginFilters[keyof typeof userLastLoginFilters]

export const isUserLastLoginFilter = (
  value: string
): value is UserLastLoginFilter =>
  (Object.values(userLastLoginFilters) as readonly string[]).includes(value)

export const userAccesses = {
  ALL_FILES: 'all_files',
  LIMITED: 'limited',
} as const

export type UserAccess = typeof userAccesses[keyof typeof userAccesses]

export const userColumns = {
  NAME: 'name',
  STATUS: 'status',
  ROLE: 'role',
  ACCESS: 'access',
  FILES: 'files',
  LAST_LOGIN: 'last_login',
  ACTIONS: 'actions',
} as const

export type UserColumn = typeof userColumns[keyof typeof userColumns]

// The files count is not one of these. UserRow counts the files of a page after
// the list query has run, so the server has no column to order the firm by, and
// whether the column needs a sort at all is open in #34115.
export const sortableUserColumns = [
  userColumns.NAME,
  userColumns.LAST_LOGIN,
] as const

export type SortableUserColumn = typeof sortableUserColumns[number]

export const isSortableUserColumn = (
  column: string
): column is SortableUserColumn =>
  (sortableUserColumns as readonly string[]).includes(column)

export const userSortDirections = {
  ASC: 'asc',
  DESC: 'desc',
} as const

export type UserSortDirection =
  typeof userSortDirections[keyof typeof userSortDirections]

export interface UserRow {
  id: number
  name: string
  email: string
  status: UserStatus
  role: UserRole
  access: UserAccess
  filesCount: number
  lastLogin: string | null
  certified: boolean
}

export interface PaginationMeta {
  currentPage: number
  totalPages: number
  totalCount: number
}

export interface UsersListResponse {
  users: UserRow[]
  pagination: PaginationMeta
}
